import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import { useUser } from '../utils/UserContext';
import FilterChips from '../components/shared/FilterChips';
import { cssUrl } from '../utils/cssVersion';
import {
  CalendarIcon, ClockIcon, UserIcon, SwapIcon,
  CloseIcon, DocumentIcon, UploadIcon, DollarIcon
} from '../components/shared/Icons';
import {
  fetchSouthyarra, handleSouthyarraSubmit, downloadSouthyarra,
  deleteSouthyarraRecords,
} from '../utils/api';
import { formatCurrency, parseCurrency, convertToHours } from '../utils/format';
import { rolesAccess } from '../data/map';
import { CHIP_GROUP_COLORS } from '../components/shared/chipColors';
import ChangeCleaner from '../components/ChangeCleaner'; 

const filterLeaveStyles = cssUrl('filterTemplate.css');

const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();


const SouthYarraCard = ({ item, onClick, isAdmin }) => (
  <div className="fr-card" onClick={() => onClick(item)}>
    <div className="fr-card-header">
      <div className="fr-card-dates">
        <CalendarIcon />
        <span className="fr-card-date-start">{displayDate(item[3])}</span>
        <span className="fr-card-date-sep">→</span>
        <span className="fr-card-date-end">{displayDate(item[29])}</span>
      </div>
      <div className="fr-card-status-badge-room" data-status={item[2]?.toLowerCase()}>
        {item[2]}
      </div>
    </div>

    <div className="fr-card-body">
      <div className="fr-card-row">
        <SwapIcon />
        <span className="fr-card-label">Room Type:</span>
        <span className="fr-card-value">{item[5]}</span>
        <div className="fr-card-status-badge-room" data-status={item[4]?.toLowerCase()}>
          {item[4]}
        </div>
      </div>

      <div className="fr-card-row">
        <ClockIcon />
        <span className="fr-card-label">Work Status:</span>
        <span className="fr-card-value">{item[10]}</span>
        <div className="fr-card-status-badge-room" data-status={item[6]?.toLowerCase()}>
          {item[6] === "Yes" ? "Bedroom" : "-"}
        </div>
      </div>

      <div className="fr-card-row">
        <UserIcon />
        <span className="fr-card-label">Cleaner:</span>
        <span className="fr-card-value">{item[15] === 'Cleaner Name' ? 'Unclaimed — tap to claim' : item[15]}</span>
        <div className="fr-card-status-badge-room" data-status={item[7]?.toLowerCase()}>
          {item[7] === "Yes" ? "Bathroom" : "-"}
        </div>
      </div>

      <div className="fr-card-row">
        <UserIcon />
        <span className="fr-card-label">Email:</span>
        <span className="fr-card-value">{item[18]}</span>
        <div className="fr-card-status-badge-room" data-status={item[8]?.toLowerCase()}>
          {item[8] === "Yes" ? "Kitchen" : "-"}
        </div>
      </div>
    </div>

    <div className="fr-card-footer">
      <div className="fr-card-meta">
        <span className="fr-card-meta-item">
          <DollarIcon />
          Allocated: {formatCurrency(parseCurrency(item[17]))} &nbsp; Same Day: {item[12]} &nbsp; Extrawork: {formatCurrency(parseCurrency(item[23]))}
          {isAdmin && <>&nbsp; Hours Worked: {convertToHours(parseCurrency(item[16]))}</>}
        </span>
      </div>
    </div>

    <div className="fr-card-tap-hint">Tap to update departure record</div>
  </div>
);


const SouthYarraModal = ({ item, onClose, onSubmit, isSubmitting, onToast, isAdmin, user }) => {

  const sanitize = (val) => String(val).replace(/[^0-9.]/g, '');
  const [workStatus, setWorkStatus] = useState(item?.[10] || '');
  const [completedDate, setCompletedDate] = useState(item?.[29] || '');
  const [cleanDate] = useState(item?.[3] || '');
  const [typeRoom, setTypeRoom] = useState(item?.[5] || '');
  const val = item?.[22];
  const clean = val != null ? sanitize(val) : '';
  const initial = clean === "0" ? "0.00"
  : clean ? (Number(clean) / 60).toFixed(2)
  : '';
  const [extraWork, setExtraWork] = useState(initial);
  const extraWorkChange = initial;
  const [cleaner, setCleaner] = useState(
    item?.[15] === "Cleaner Name" ? (user?.userName || '') : (item?.[15] || '')
  );
  const [email, setEmail] = useState(
    item?.[18] === "enteryouremail@yahoo.com.au" ? (user?.email || '') : (item?.[18] || '')
  );
  const LOCKED_STATUSES = ['Ready for invoicing', 'Invoiced'];
  const isApproved = item?.[13] === 'Approved';
  const isLocked = isApproved ||
    (!isAdmin && LOCKED_STATUSES.includes(item?.[10]));
  const roomType = [
    'Departure Studio', 'Departure Multishared', 'Common Area Clean',
    'Steam Clean', 'Corrective Clean Studio', 'Corrective Clean Multishared'
  ];
  const workstatus = [
    'Pending', 'Cleaning in progress', 'Cleaning Completed',
    'Ready for invoicing', 'Invoiced', 'Please Delete', 'On hold', 'Supervisor'
  ];

  const workstatuscleaner = [
    'Pending', 'Cleaning in progress', 'Cleaning Completed','Please Delete', 'On hold', 'Supervisor'
  ];

  const workday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const extra_day = new Date(cleanDate);
  const myExtraDay = workday[extra_day.getDay()];
  const [extraWorkDay, setExtraWorkDay] = useState(myExtraDay);
  const workingStatus = isAdmin ? workstatus : workstatuscleaner;

  // ─────────────────────────────────────────────
  //  COMPLETED DATE HANDLER — anchor-based
  // ─────────────────────────────────────────────
  const handleCompletedDateChange = (val) => {
    if (!val) {
      setCompletedDate('');
      return;
    }

    if (!cleanDate) {
      setCompletedDate(val);
      return;
    }

    if (val < cleanDate) {
      onToast({ message: 'Completed date cannot be before the clean date.', type: 'warning' });
      return;
    }

    const [cy, cm] = cleanDate.split('-').map(Number);
    const [vy, vm] = val.split('-').map(Number);
    if (cy !== vy || cm !== vm) {
      const lastDay = getLastDayOfMonth(cy, cm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const clamped = `${cy}-${pad(cm)}-${pad(lastDay)}`;
      onToast({
        message: `Completed date must be in the same month as the clean date. Clamped to ${lastDay}th.`,
        type: 'warning',
      });
      setCompletedDate(clamped);
      return;
    }

    setCompletedDate(val);
  };

  const validateOnSubmit = () => {
    if (!completedDate || !cleanDate) return true;
    if (completedDate < cleanDate) {
      onToast({ message: 'Completed date cannot be before the clean date.', type: 'warning' });
      return false;
    }
    const [cy, cm] = cleanDate.split('-').map(Number);
    const [vy, vm] = completedDate.split('-').map(Number);
    if (cy !== vy || cm !== vm) {
      onToast({ message: 'Completed date must be in the same month and year as the clean date.', type: 'warning' });
      return false;
    }
    return true;
  };

  const convertToMinutes = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * 60);
};

  const getExtraWorkUser = () => {
    return extraWork !== extraWorkChange ? user.userName : "Noadmin";
  };
const toNumberString = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const cleaned = String(val).replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return '';
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : String(num);
  };
  const handleSubmit = () => {
    if (!validateOnSubmit()) return;
    onSubmit({
      recordId: item[0],
      work_status: workStatus,
      complete_date: completedDate,
      cleaner_user: cleaner,
      email_user: email,
      extra_work: toNumberString(convertToMinutes(extraWork)),
      extra_work_change: extraWorkChange,
      extra_work_day: extraWorkDay,
      mylogin: getExtraWorkUser(),
    });
  };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Update Departure</h3>
          <button className="fr-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="fr-modal-body">
          <div className="fr-modal-field">
            <label className="fr-modal-label">Allocated</label>
            <div className="fr-modal-value">{formatCurrency(parseCurrency(item[17]))}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Clean Date</label>
            <div className="fr-modal-value">{displayDate(item[3])}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Room Number</label>
            <div className="fr-modal-value">{item[2]}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Room Type</label>
            <div className="fr-modal-value">{item[5]}</div>
          </div>

          <CustomSelect
            label="Work Status"
            value={workStatus}
            onChange={setWorkStatus}
            options={workingStatus}
          />

          <div className="fr-modal-field">
            <label className="fr-modal-label">Completed Date</label>
            <input
              type="date"
              className="fr-modal-input"
              value={completedDate}
              onChange={(e) => handleCompletedDateChange(e.target.value)}
            />
          </div>

          <div className="fr-modal-field">
            <label className="fr-modal-label">Cleaner</label>
            <div className="fr-modal-value">{cleaner}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Email</label>
            <div className="fr-modal-value">{email}</div>
          </div>
          {(isAdmin || user.userJob === "Supervisor") && (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Extra work</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  inputMode="decimal"
                  className="fr-modal-input"
                  value={extraWork}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || Number(v) >= 0) setExtraWork(v);
                  }}
                  placeholder="0.00"
                />
              </div>
              <CustomSelect
                label="Select Day"
                value={extraWorkDay}
                onChange={setExtraWorkDay}
                options={workday}
              />
            </>
          )}
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" onClick={onClose}>
            Close
          </button>
          <button
            className="fr-modal-btn fr-btn-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || isLocked}
          >
            {isApproved ? '🔒 Approved — final'
            : isLocked ? '🔒 Ready for invoicing'
            : isSubmitting ? 'Processing...'
            : 'Update Departure'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── DELETE CONFIRMATION MODAL ───
const DeleteConfirmModal = ({ recordIds, rooms, onProceed, onCancel, isDeleting }) => (
  <div className="fr-modal-overlay" onClick={onCancel}>
    <div className="fr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
      <div className="fr-modal-handle" />
      <div className="fr-modal-header">
        <h3 className="fr-modal-title">Confirm Delete</h3>
        <button className="fr-modal-close" onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>
      <div className="fr-modal-body">
        <p style={{ fontSize: '0.9rem', color: '#f1f5f9', marginBottom: '12px' }}>
          You are about to delete <strong style={{ color: '#f87171' }}>{recordIds.length}</strong>{' '}
          record{recordIds.length === 1 ? '' : 's'} marked as "Please Delete".
        </p>
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
          A summary email will be sent before the records are removed from Airtable.
        </p>
        <div style={{
          background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: '10px',
          padding: '10px 14px',
          maxHeight: '180px',
          overflowY: 'auto',
        }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#f87171', marginBottom: '6px' }}>
            Records to delete:
          </div>
          {rooms.slice(0, 50).map((r, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: '#fca5a5', marginBottom: '3px' }}>
              • Room {r.room} — {displayDate(r.cleanDate)}
            </div>
          ))}
          {rooms.length > 50 && (
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              …and {rooms.length - 50} more
            </div>
          )}
        </div>
      </div>
      <div className="fr-modal-actions">
        <button className="fr-modal-btn fr-btn-cancel" onClick={onCancel} disabled={isDeleting}>
          Cancel
        </button>
        <button
          className="fr-modal-btn fr-btn-submit"
          onClick={onProceed}
          disabled={isDeleting}
          style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}
        >
          {isDeleting ? 'Deleting…' : 'Proceed'}
        </button>
      </div>
    </div>
  </div>
);


const filterChipGroups = [
  { color: CHIP_GROUP_COLORS.cyan,   values: ['Today'] },
  { color: CHIP_GROUP_COLORS.violet, values: ['Pending', 'Inprogress', 'Completed', 'ReadyInvoicing', 'Invoiced', 'Delete', 'Hold'] },
  { color: CHIP_GROUP_COLORS.amber,  values: ['DeptStudio', 'DeptMulti', 'Common', 'SteamClean', 'CorrectStudio', 'CorrectMulti'] },
  { color: CHIP_GROUP_COLORS.rose,   values: ['Approved', 'Pending Approval'] },
];

const FilterSouthYarra = ({ showHeader = false }) => {
  const { user } = useUser();
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');
  const isManagement = rolesAccess.management.includes(user?.userJob || '');
  

  // ── State for the delete flow ──
  const [allItems, setAllItems] = useState([]);
  const [chipQuery, setChipQuery] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localToast, setLocalToast] = useState(null);

  const CORRECTION_ROLES = [
  'IT Operations', 'Contract Administrator', 'Accounts',
  'Managing Director', 'Operations Director', 'Chief Executive Officer',
  'Supervisor',
];
  const canCorrect = CORRECTION_ROLES.includes(user?.userJob || '');
  const refetchRef = useRef(null);

  const handleItemsChange    = useCallback((items) => setAllItems(Array.isArray(items) ? items : []), []);
  const handleChipQueryChange = useCallback((q) => setChipQuery(q || ''), []);

  // ── Records pending delete: admin + 'Delete' chip active + item[26] === 'Delete' ──
  const pendingDelete = useMemo(() => {
    if (!isAdmin) return [];
    // FilterChips lowercases values, so check case-insensitively
    if (!(chipQuery || '').toLowerCase().includes('delete')) return [];
    if (!Array.isArray(allItems) || allItems.length === 0) return [];

    return allItems
      .filter((row) => row[26] === 'Delete')
      .map((row) => ({
        recordId: row[0],
        room: row[2],
        cleanDate: row[3],
      }));
  }, [allItems, chipQuery, isAdmin]);

  const handleDeleteConfirmed = async () => {
    if (pendingDelete.length === 0) return;
    setIsDeleting(true);
    try {
      const res = await deleteSouthyarraRecords(pendingDelete);
      if (res?.success) {
        setShowConfirm(false);
        
        if (refetchRef.current) await refetchRef.current();
        setLocalToast({ type: 'success', message: res.message || 'Records deleted successfully.' });
      } else {
        throw new Error(res?.message || 'Delete failed');
      }
    } catch (err) {
      setLocalToast({ type: 'error', message: err.message || 'Failed to delete records.' });
    } finally {
      setIsDeleting(false);
    }
  };

  // Toast auto-dismiss
  useEffect(() => {
    if (localToast) {
      const t = setTimeout(() => setLocalToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [localToast]);

  
  const renderDeleteAction = () => {
    if (!isAdmin || pendingDelete.length === 0) return null;
    return (
      <button
        onClick={() => setShowConfirm(true)}
        title={`Delete ${pendingDelete.length} record${pendingDelete.length === 1 ? '' : 's'}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          borderRadius: '8px',
          background: 'rgba(239,68,68,0.12)',
          border: '1px solid rgba(239,68,68,0.35)',
          color: '#f87171',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(239,68,68,0.2)';
          e.currentTarget.style.borderColor = 'rgba(239,68,68,0.55)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
          e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)';
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
        </svg>
        Delete {pendingDelete.length}
      </button>
    );
  };

  return (
    <>
      {localToast && (
        <div className={`ob-toast ${localToast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}
             style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {localToast.message}
        </div>
      )}

      <FilterTemplate
        title="South Yarra"
        helpButton={
          <HelpButton helpData={[
            { chip: 'southyarra_filter', urlFilter: '/static/pdffilters/SouthyarraFilter.pdf' }
          ]} />
        }
        subtitle="Room Cleaning"
        searchPlaceholder="Search by fields..."
        stylesheetHref={filterLeaveStyles}
        fetchData={fetchSouthyarra}
        dataKey="southyarra"
        searchFields={[2, 3, 15, 18, 26, 27, 28]}
        onSubmit={handleSouthyarraSubmit}
        loadingMessage="Loading southyarra..."
        showNavigation={true}
        showHeader={showHeader}
        chipLogic="and"
        todayFilterIndex={3}
        onDownload={(isAdmin || isManagement) && downloadSouthyarra}
        onChipQueryChange={handleChipQueryChange}
        onItemsChange={handleItemsChange}
        onRefetchRef={refetchRef}
        renderExtraActions={() => (
          <>
            <ChangeCleaner items={allItems} visible={canCorrect}
              onSaved={() => refetchRef.current?.()} onToast={setLocalToast} />
            {renderDeleteAction()}
          </>
        )}
        renderChips={(cq, setCq, chipsOpen, setChipsOpen) => (
          <FilterChips
            chipGroups={filterChipGroups}
            chipQuery={cq}
            setChipQuery={setCq}
            chipsOpen={chipsOpen}
            setChipsOpen={setChipsOpen}
          />
        )}
        renderCard={(item, onClick) => (
          <SouthYarraCard item={item} onClick={onClick} isAdmin={isAdmin}/>
        )}
        renderModal={(item, onClose, onSubmit, isSubmitting, setToast) => (
          <SouthYarraModal
            item={item}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onToast={setToast}
            isAdmin={isAdmin}
            user={user}
          />
        )}
      />

      {showConfirm && (
        <DeleteConfirmModal
          recordIds={pendingDelete.map((p) => p.recordId)}
          rooms={pendingDelete}
          onProceed={handleDeleteConfirmed}
          onCancel={() => setShowConfirm(false)}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default FilterSouthYarra;